import { PRESENTER_TOOLS } from "@models/multiuser/IMultiuserSessionModel";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import type { ToolId } from "./IToolbarModel";

/**
 * Which toolbar tools this user may use — the ONE mapping from a web `ToolId`
 * to the permission that governs it, shared by the toolbar model (which refuses
 * and disarms a denied tool), `usePermissions` (which filters the palette) and
 * the runtime tool controllers (which re-check before acting on input).
 *
 * It used to live in `usePermissions`, which left the model layer unable to ask
 * the question at all: disarming a tool that lost its permission ran in a
 * `useToolbar` watcher, so it only happened while some panel holding a
 * `useToolbar` was mounted — and during a workflow run most of them are not.
 */

/**
 * The tool a denied one falls back to — verbatim from Unity's desktop
 * `FooterViewModel.ApplyEditorMode` tail (`:1074-1077`), which sets
 * `IsPointerMode = true` whenever the armed tool stops being allowed, without
 * consulting the role.
 *
 * ⚠ The pointer is NOT always permitted (`ActiveToolSet.ShowPointer` is
 * `canAuthorProject()`), so this can legitimately land on a tool the user may
 * not have — and that is the intended shape, not an oversight. A denied pointer
 * is INERT for authoring: it is filtered out of the palette, and
 * `selectToolAvailability.isSelectToolActive` carries the same gate, so
 * selection picks, drags and gizmo attach all go quiet. Falling back to the
 * first ALLOWED tool instead would arm the laser for a viewer who never asked
 * for it, which suppresses their camera input.
 */
export const K_FALLBACK_TOOL: ToolId = "select";

/**
 * Map a web `ToolId` to its Unity `PresenterTools` flag. The same value rides
 * the multiuser `at` presence field, so `registrations.ts` announces the armed
 * tool through this function too — one mapping, so a tool cannot be gated
 * without being announced or announced without being gated.
 */
export function presenterToolFor(tool: ToolId): number {
  switch (tool) {
    case "laser": return PRESENTER_TOOLS.LaserTool;
    case "comment": return PRESENTER_TOOLS.AnnotationTool;
    case "slice": return PRESENTER_TOOLS.SliceTool;
    case "sketch": return PRESENTER_TOOLS.SketchTool;
    case "measure": return PRESENTER_TOOLS.BoundingBoxTool;
    case "grab": return PRESENTER_TOOLS.GrabTool;
    case "select":
    default: return PRESENTER_TOOLS.None;
  }
}

/**
 * Is `tool` available to this user right now?
 *
 * Every tool but one answers through `CanUseTool`. The POINTER is the
 * exception, upstream as well as here: `PresenterTools.None` takes
 * `CanUseTool`'s ungated `default: return true` arm, so Unity gives it its own
 * rule one layer up — `ActiveToolSet.ShowPointer` (`:87-90`) is
 * `IsPresenter && UserRole.CanAuthorProject()`, which is exactly
 * `PermissionsModel.CanAuthorProject()`, the strictest gate in the palette.
 *
 * While the user has handed the presenter role to Spark (a workflow run or an
 * author's preview — `IPermissionsModel.isPresenterDelegated`) both predicates
 * answer false for every tool, so this does too, with no run term of its own.
 *
 * FAILS CLOSED: no permissions model means no tool.
 */
export function isToolbarToolAllowed(
  permissions: IPermissionsModel | null,
  tool: ToolId,
): boolean {
  if (!permissions) return false;
  if (tool === "select") return permissions.canAuthorProject();
  return permissions.canUseTool(presenterToolFor(tool));
}

/**
 * May this user Undo / Redo? Verbatim `FooterViewModel.CanUndo`
 * (`FooterViewModel.cs:1053` / `:1061` / `:1069`): `CanBePresenter()`, forced
 * false when `IsRightless()`.
 *
 * ⚠ Presenter ELIGIBILITY, not "is presenting", deliberately: someone who has
 * just handed presentership over keeps Undo so they can take back a change made
 * during their own turn. Do not tighten it to `isPresenter` or
 * `canAuthorProject` for consistency with the write gates.
 *
 * No delegation term: while a workflow run or an author's preview holds the
 * presenter role, `IUndoRedo.suspend` already makes undo / redo a no-op
 * (`presenterDelegationEffects`), whatever this answers.
 *
 * The ONE definition, read by the toolbar model (which refuses the command —
 * the gate the keyboard shortcut relies on) and by `usePermissions.canUndo`
 * (which hides the Undo buttons).
 *
 * FAILS CLOSED: no permissions model means no undo.
 */
export function isUndoAllowed(permissions: IPermissionsModel | null): boolean {
  if (!permissions) return false;
  return permissions.canBePresenter() && !permissions.isRightless();
}
